<template>
<div>
    <div class="loading" v-if="fetchData">
        <load-data></load-data>
    </div>

    <div class="examForm p-2" v-else>
        <b-overlay :show="loadExams" spinner-variant="primary" spinner-type="grow" spinner-small rounded="md">

            <!-- *************** Public Exam Creation **************** -->
            <div v-if="exam.course_id == 0">
                <PublicExamCreated
                    @examFormCancelled="cancel()"
                    :exam="exam"
                    @getExams="getExams()"
                    :publicexamToBeUpdated="publicexamToBeUpdated"
                />
            </div>
            <!-- *************** Public Exam Creation **************** -->

            <b-form @submit.prevent="prepareExamPostDataForApi()" v-if="exam.course_id != 0">

                <!-- Select Course -->
                <b-form-group>
                    <label class="required-label">Select Course:</label>
                    <b-form-select
                        :options="coursesOptions"
                        v-model="exam.course_id"
                        @change="loadActivities();loadActivityTypes();"
                        :class="{ 'form-group-error': $v.exam.course_id.$error }"
                        :disabled="courseDisabled"
                    />
                </b-form-group>

                <!-- Activity Name -->
                <b-form-group>
                    <label class="required-label">Activity Name :</label>

                    <b-form-input
                        v-if="activitiesOptions.length === 0"
                        placeholder="Enter Activity Name..."
                        v-model="exam.name"
                        :class="[{ 'form-group-error': $v.exam.name.$error }, 'required']"
                    />

                    <div v-else>
                        <b-form-select
                            :options="activitiesOptions"
                            v-model="exam.activity_name_id"
                            :class="{ 'form-group-error': $v.exam.activity_name_id.$error }"
                        />
                        <br />
                        <b-form-input
                            v-if="exam.activity_name_id == 0"
                            placeholder="Enter Activity Name"
                            v-model="exam.name"
                            :class="[{ 'form-group-error': $v.exam.name.$error }, 'required']"
                        />
                    </div>
                </b-form-group>

                <!-- Description -->
                <b-form-group label="Description:">
                    <b-form-textarea rows="3" v-model="exam.summary" />
                </b-form-group>

                <!-- Activity Type (Create) -->
                <b-form-group v-if="!examToBeUpdated.id">
                    <label class="required-label">Select Activity Type:</label>
                    <b-form-select v-model="exam.activity_type" :options="activityOptions" />
                </b-form-group>

                <!-- Activity Type (Edit - Readonly) -->
                <b-form-group label="Activity Type" v-if="examToBeUpdated.id">
                    <p class="text-info font-weight-bold">
                        {{ activityTypeLabel }}
                    </p>
                </b-form-group>

                <!-- Activity Group -->
                <b-form-group 
                v-if="exam.activity_type !== 'tutorial' && !hideActivityGroup"
                label="Select Activity Group">
                    <b-form-select v-model="exam.category_id" :options="examCategoriesOptions" />
                </b-form-group>

                <!-- Course Linked Assessment (ONLY real assessment) -->
                <b-form-group
                    v-if="exam.activity_type === 'assessment'"
                    label="Course Linked Assessment"
                >
                    <b-form-checkbox switch v-model="courseLinkedAssessment" />
                </b-form-group>

                <!-- Participants -->
                <b-form-group v-if="!courseLinkedAssessment">
                    <UserubgroupUsers />
                </b-form-group>

                <!-- Online / Offline (NOT for new activity types) -->
                <b-form-group
                    v-if="!hideCourseLinkedAndOffline && exam.activity_type !== 'tutorial'"
                    label="Online / Offline Activity"
                >
                    <b-form-checkbox switch v-model="exam.offline_activity" />
                    <span class="text-info">
                        {{ exam.offline_activity ? 'Offline' : 'Online' }}
                    </span>
                </b-form-group>

                <!-- Date Label -->
                <b-form-group>
                    <label :class="{ 'required-label': !exam.offline_activity }">
                        {{
                            isAssessmentLike
                                ? exam.offline_activity
                                    ? 'Select Date'
                                    : 'Select Date and Time'
                                : 'Select Last Date of Submission'
                        }}
                    </label>

                    <b-form-datepicker v-model="date" />

                    <!-- Time -->
                    <div
                        v-if="isAssessmentLike && !exam.offline_activity"
                        class="d-flex mt-2"
                    >
                        <b-form-timepicker v-model="examStart" placeholder="Start Time" />
                        <b-form-timepicker v-model="examEnd" placeholder="End Time" class="ml-3" />
                    </div>
                </b-form-group>

                <!-- MCQ Options -->
                <div v-if="exam.is_objective_only && !exam.offline_activity">
                    <b-form-checkbox switch v-model="exam.shuffle_questions">
                        Shuffle Questions
                    </b-form-checkbox>
                    <b-form-checkbox switch v-model="exam.shuffle_choices">
                        Shuffle Answers
                    </b-form-checkbox>
                    <b-form-checkbox switch v-model="exam.auto_evaluation">
                        Auto Evaluation
                    </b-form-checkbox>
                </div>

                <!-- Assignment / Tutorial -->
                <b-form-group v-if="!isAssessmentLike" label="Date of Return">
                    <b-form-datepicker v-model="return_at_date" />
                </b-form-group>

                <!-- Common Options -->
                <b-form-group label="Make Activity Hidden for Students">
                    <b-form-checkbox switch v-model="exam.hidden" />
                </b-form-group>

                <b-form-group label="Exclude from report">
                    <b-form-checkbox switch v-model="exam.exclude_from_report" />
                </b-form-group>

                <!-- Buttons -->
                <b-form-group>
                    <div class="d-flex justify-content-between">
                        <b-button type="submit" variant="primary" :disabled="examBtn.disabled">
                            {{ examBtn.text }}
                        </b-button>
                        <b-button variant="danger" @click="cancel()">
                            Cancel
                        </b-button>
                    </div>
                </b-form-group>

            </b-form>
        </b-overlay>
    </div>
</div>
</template>

<script>
import {
    required,
    requiredIf
} from "vuelidate/lib/validators";
import moment from "moment";
import UserubgroupUsers from "./UserubgroupUsers.vue";
import PublicExamCreated from "../public_exam/PublicExamCreation.vue";
export default {
    props: ["examToBeUpdated","publicexamToBeUpdated"],
    components: {
        UserubgroupUsers,
        PublicExamCreated,
    },
    async created() {
        this.fetchData = true;
        this.$set(this.exam, "course_id", null);
        this.$set(this.exam, "co_score_evaluation", false);
        this.$set(this.exam, "offline_activity", true);
        this.$set(this.exam, "activity_type", null);
        this.$set(this.exam, "summary", null);
        this.$set(this.exam, "respond_after_close", false);
        this.$set(this.exam, "exclude_from_report", false);
        this.$set(this.exam, "is_objective_only", false);
        this.$set(this.exam, "shuffle_questions", false);
        this.$set(this.exam, "shuffle_choices", false);
        this.$set(this.exam, "auto_evaluation", false);
        this.$set(this.exam, "publish_result", false);
        this.$set(this.exam, "activity_name_id", null);
        this.$set(this.exam, "enableactivityselect", false);
        this.hoursOptions = [{
            value: null,
            text: "Select Hour",
            disabled: true
        }];
        for (let hour = 0; hour <= 12; hour++) {
            this.hoursOptions.push({
                value: hour < 10 ? "0" + hour : hour,
                text: hour < 10 ? "0" + hour : hour,
            });
        }
        this.minutesOptions = [{
            value: null,
            text: "Select Minute",
            disabled: true
        }, ];
        for (let min = 0; min <= 60; min++) {
            this.minutesOptions.push({
                value: min < 10 ? "0" + min : min,
                text: min < 10 ? "0" + min : min,
            });
        }

        await this.getExamCategories();
        // await this.getMaximumAttemptsAndChoices();
        await this.getCourses();

        if (this.examToBeUpdated.id != "") {
            // const examData = await this.examToBeUpdated.exam;
            // this.exam = await this.examToBeUpdated.exam;
            this.exam.activity_name_id = this.examToBeUpdated.exam.activity_name_id;
            if (!this.examToBeUpdated.exam.positive_mark) {
                this.differentScores = true;
            }
            for (var key in this.examToBeUpdated.exam) {
                this.$set(this.exam, key, this.examToBeUpdated.exam[key]);
            }
            let seconds = this.examToBeUpdated.exam.limitted_time_seconds;
            if (seconds && seconds > 0) {
                this.limittedtimeExam = true;
                var hours = Math.floor(seconds / 3600);
                seconds = seconds - hours * 3600;
                var minute = Math.floor(seconds / 60);
                this.selectedHour = hours < 10 ? `0${hours}` : `${hours}`;
                this.selectedMinute = minute < 10 ? `0${minute}` : `${minute}`
            }
            this.exam.offline_activity = this.examToBeUpdated.exam.offline_activity == 1 ? true : false;

            this.courseDisabled = true;
            let courseId = await this.coursesOptions.findIndex((course) => {
                if (course.value == this.exam.course_id) {
                    this.exam.usergroup_id = course.usergroup_id
                }
                return course.value == this.exam.course_id;
            });
            this.loadActivities();
            this.loadActivityTypes();
            this.selectedCourse = this.coursesOptions[courseId].text;
            this.courseLinkedAssessment = this.exam.course_id ? true : false;
            await this.exam.starts_at;
            let theDate = await moment(
                moment.utc(this.exam.starts_at, "YYYY-MM-DD HH:mm:ss").toDate()
            );
            moment.utc(this.exam.starts_at).local();

            this.date = new Date(theDate.format("YYYY/MM/DD"));

            this.examStart = moment(
                moment.utc(this.exam.starts_at, "YYYY-MM-DD HH:mm:ss").toDate()
            ).format("HH:mm");
            this.examEnd = moment(
                moment.utc(this.exam.end_at, "YYYY-MM-DD HH:mm:ss").toDate()
            ).format("HH:mm");

            if (this.exam.activity_type != 'assessment') {
                let returnDate = await moment(this.exam.return_at, "YYYY-MM-DD HH:mm:ss");
                this.return_at_date = new Date(returnDate.format("YYYY/MM/DD"));
                let endDate = await moment.utc(this.exam.end_at, "YYYY-MM-DD HH:mm:ss").local();
                this.date = new Date(endDate.format("YYYY/MM/DD"));
            }
        }
       
        this.$forceUpdate();
    },
    data() {
        return {
            fetchData: false,
            loadExams: false,
            activitiesOptions: [],
            disable_manual_activity_name: 0,
            enableactivityselect: false,
            courseDisabled: false,
            differentScores: false,
            exam: {},
            date: "",
            theDate: "",
            examStart: "",
            examEnd: "",
            return_at_date: "",
            limittedtimeExam: false,
            maxTimeAllowedToCompleteExam: {
                status: false,
                hours: "",
                minutes: "",
            },
            courseLinkedAssessment: true,
            examBtn: {
                text: "Submit",
                disabled: false,
            },
            examCategoriesOptions: [],
            // maximumAttemptsAllowed: [],
            // maxChoicesAllowed: [],
            coursesOptions: [],
           allActivityTypes: [
                { value: "assessment", text: "Assessment" },
                { value: "assignment", text: "Assignment" },
                { value: "flipped_class", text: "Flipped Class" },
                { value: "listening_task", text: "Listening Task" },
                { value: "models", text: "Models" },
                { value: "quiz", text: "Quiz" },
                { value: "reading_task", text: "Reading Task" },
                { value: "speaking_task", text: "Speaking Task" },
                { value: "tutorial", text: "Tutorial" },
                { value: "writing_task", text: "Writing Task" },
            ],
            selectedCourse: null,
            hoursOptions: [],
            minutesOptions: [],
            selectedHour: null,
            selectedMinute: null,
            selectedActivityTypes: [],
        };
    },
    validations: {
        date: {
            required: requiredIf(function () {
                if (this.exam.activity_type != "tutorial" && !this.exam.offline_activity) {
                    return true;
                }
            }),
        },
        examStart: {
            required: requiredIf(function () {
                if (this.exam.activity_type == "assessment" && !this.exam.offline_activity) {
                    return true;
                }
            }),
        },
        examEnd: {
            required: requiredIf(function () {
                if (this.exam.activity_type == "assessment" && !this.exam.offline_activity) {
                    return true;
                }
            }),
        },
        // return_at_date: {
        //   required: requiredIf(function () {
        //     if (this.exam.activity_type == "tutorial") {
        //       return true;
        //     }
        //   }),
        // },
        exam: {
            name: {
                required: requiredIf(function () {
                    if (!this.exam.activity_name_id) {
                        return true;
                    }
                }),
            },
            activity_name_id: {
                required: requiredIf(function () {
                    if (!this.exam.name) {
                        return true;
                    }
                }),
            },
            activity_type: {
                required,
            },
            course_id: {
                required,
            },
        },
    },

    computed: {
        activityOptions() {
            const options = this.allActivityTypes
                .filter(type => this.selectedActivityTypes.includes(type.value))
                .sort((a, b) => a.text.localeCompare(b.text));

            return [
                { value: null, text: "Select Activity Type" },
                ...options
            ];
        },
        isAssessmentLike() {
            return [
                'assessment',
                'flipped_class',
                'models',
                'listening_task',
                'speaking_task',
                'reading_task',
                'writing_task',
                'quiz'
            ].includes(this.exam.activity_type)
        },

        hideCourseLinkedAndOffline() {
            return [
                'flipped_class',
                'models',
                'listening_task',
                'speaking_task',
                'reading_task',
                'writing_task',
                'quiz'
            ].includes(this.exam.activity_type)
        },

        hideActivityGroup() {
            return [
                'flipped_class',
                'models',
                'listening_task',
                'speaking_task',
                'reading_task',
                'writing_task',
                'quiz'
            ].includes(this.exam.activity_type)
        },

        activityTypeLabel() {
            const opt = this.activityOptions.find(
                o => o.value === this.exam.activity_type
            )
            return opt ? opt.text : '-'
        }
    },


    watch: {
        "exam.course_id": function () {
            this.coursesOptions.forEach(course => {
                if (course.value == this.exam.course_id) {
                    this.exam.usergroup_id = course.usergroup_id;
                    this.getUsergroup();
                }
            });
            this.exam.enableactivityselect = false;
        },
        "exam.activity_name_id": function () {
            if (this.exam.activity_name_id > 0) {
                this.exam.enableactivityselect = true;
                this.activitiesOptions.forEach(activity => {
                    if (activity.value == this.exam.activity_name_id) {
                        this.exam.name = activity.text;
                    }
                });
            }
        },
    },
    methods: {
        // changePublishResult() {
        //   if (this.exam.publish_result) this.exam.auto_evaluation = true;
        // },
        async loadActivityTypes() {
            try {
                const res = await this.$axios.get(
                `${this.$store.getters.getAPIKey.mainAPI}/usergroups/setting/${this.exam.usergroup_id}/activity_types`
                );

                if (Array.isArray(res.data)) {
                this.selectedActivityTypes = res.data;
                } else {
                // default fallback
                this.selectedActivityTypes = ['assessment', 'assignment', 'tutorial'];
                }
            } catch (e) {
                this.selectedActivityTypes = ['assessment', 'assignment', 'tutorial'];
            }
        },
        async getUsergroup() {
            const url =
                this.$store.getters.getAPIKey.mainAPI +
                `/usergroups/${this.exam.usergroup_id}`;
            await this.$axios
                .get(url)
                .then((response) => {
                    this.disable_manual_activity_name = response.data.disable_manual_activity_name;
                })
                .catch((error) => {
                    error;
                });
        },
        async loadActivities() {
            const url = this.$store.getters.getAPIKey.mainAPI + `/activitysettings?usergroup_id=${this.exam.usergroup_id}`;
            await this.$axios
                .get(url)
                .then((response) => {
                    this.activitiesOptions = [];
                    let activities = response.data;
                    activities.forEach((activity) => {
                        this.activitiesOptions.push({
                            value: activity.id,
                            text: activity.activity_name
                        });
                    });
                    if (this.activitiesOptions.length >0 && this.disable_manual_activity_name == 0) {
                        this.activitiesOptions.push({
                            value: "0",
                            text: "Add Manually"
                        });
                    }
                   
                    if (this.examToBeUpdated.id != "" && this.activitiesOptions.length > 0 && !this.exam.activity_name_id) {
                        this.exam.activity_name_id = 0;
                    }
                   
                    response;
                })
                .catch((error) => {
                    error;
                });
        },
        getTotalTime() {
            const date = this.date;
            const startT = moment(date + " " + this.examStart);
            const endT = moment(date + " " + this.examEnd);
            const tTime = moment.duration(endT.diff(startT)).asSeconds();

            // console.log(tTime);
            if (
                this.selectedHour != null &&
                this.selectedMinute != null &&
                this.limittedtimeExam
            ) {
                // console.log(
                //   moment
                //     .duration(
                //       this.selectedHour + ":" + this.selectedMinute + ":00",
                //       "hh:mm:ss"
                //     )
                //     .asSeconds()
                // );
                if (
                    tTime <=
                    moment
                    .duration(
                        this.selectedHour + ":" + this.selectedMinute + ":00",
                        "hh:mm:ss"
                    )
                    .asSeconds()
                ) {
                    this.$toast.error("Please choose a time duration less than total exam duration", {
                        duration: 3000,
                        position: "top",
                    });
                    this.selectedHour = null;
                    this.selectedMinute = null;
                }
            }
            // console.log(tTime);
        },
        //********* Prepare Exam Post Data For API **********/
        numberValidation(e) {
            if (isNaN(e)) {
                e.preventDefault();
            }
        },
        async prepareExamPostDataForApi() {
            if (this.exam.activity_name_id == 0) {
                this.exam.activity_name_id = null;
            }
            await this.$v.$touch();
            if (this.$v.$invalid) {
                const tabHeight = document.getElementById(
                    "staffListTab__BV_tab_container_"
                );
                this.$smoothScroll({
                    container: tabHeight,
                    scrollTo: document.querySelector(".form-group-error"),
                    duration: 1000,
                    offset: -120,
                });
                this.submitStatus = "ERROR";
                return;
            } // validation

            this.examBtn.text = "Please wait...";
            this.examBtn.disabled = true;
            this.loadExams = true;
            let startT;
            if (this.exam.activity_type == "assessment") {
                if (this.exam.offline_activity) {
                    startT =
                        moment(this.date).format("YYYY/MM/DD");
                    this.exam.starts_at = moment(startT, "YYYY-MM-DD").isValid() ?
                        moment(startT, "YYYY-MM-DD").format("YYYY-MM-DD") : null;
                } else {
                    startT =
                        moment(this.date).format("YYYY/MM/DD") + " " + this.examStart;
                    this.exam.starts_at =
                        startT != null ?
                        await moment(startT, "YYYY-MM-DD HH:mm:ss")
                        .utc()
                        .format() :
                        startT;
                }
            }
            let endT;
            // console.log
            if (this.exam.activity_type == "assessment" && this.examEnd && moment(this.date).isValid()) {
                endT = moment(this.date).format("YYYY/MM/DD") + " " + this.examEnd;
                this.exam.end_at = await moment(endT, "YYYY-MM-DD HH:mm:ss")
                    .utc()
                    .format();
            } else if (this.exam.activity_type != "assessment" && moment(this.date).isValid()) {
                endT = moment(this.date).format("YYYY/MM/DD") + " " + this.examEnd;
                this.exam.end_at = await moment(endT, "YYYY-MM-DD HH:mm:ss")
                    .utc()
                    .format();
            } else {
                true;
            }
            if (
                this.exam.activity_type == "assessment" &&
                this.exam.limitted_time_seconds
            ) {
                this.exam.limitted_time_seconds = moment
                    .duration(
                        moment(
                            this.date +
                            " " +
                            this.selectedHour +
                            ":" +
                            this.selectedMinute +
                            ":00"
                        ).format("hh:mm:ss")
                    )
                    .asSeconds();
            }
            if (this.exam.activity_type != 'assessment' && moment(this.return_at_date).isValid()) {
                let returnT = moment(this.return_at_date).format("YYYY/MM/DD");
                this.exam.return_at = await moment(returnT, "YYYY-MM-DD HH:mm:ss").format();
            }

            if (this.exam.activity_type == 'assessment' && this.limittedtimeExam) {
                // console.log();
                this.exam.limitted_time_seconds = 0;
                this.exam.limitted_time_seconds = Number(this.selectedHour) * 60 * 60;
                this.exam.limitted_time_seconds += Number(this.selectedMinute) * 60;
            }

            if (this.differentScores) {
                this.exam.negative_mark = null;
                this.exam.positive_mark = null;
            }

            // console.log(this.exam);
            // this.examBtn.text = "Submit";
            // this.loadExams = false;
            // this.examBtn.disabled = false;
            // return;
            let examStatus;
            if (this.examToBeUpdated.id == "") {
                examStatus = await this.createExam();
            } else {
                examStatus = await this.updateExam();
            }

            if (examStatus) {
                const msg =
                    (await this.examToBeUpdated.id) == "" ?
                    "Activity Successfully created" :
                    "Activity Successfully updated";
                this.$toast.success(msg, {
                    position: "top",
                    duration: 3000,
                });
                this.clearResponse(true);
            }
            if (!examStatus) {
                this.$toast.error("Something went wrong!", {
                    position: "top",
                    duration: 3000,
                });
                this.clearResponse(false);
            }

        },
        //********* Create Exam with API **********/
        async createExam() {
            let url = this.$store.getters.getAPIKey.examination;
            let status = null;
            await this.$axios
                .post(url, this.exam)
                .then((response) => {
                    status = true;
                    response;
                })
                .catch((error) => {
                    let errortxt = "Failed to Create";
                    if (error.response && error.response.status == 422) {
                        let errormsgs = error.response.data.errors;
                        for (const key in errormsgs) {
                            errortxt = errormsgs[key][0];
                        }
                    } else {
                        errortxt = error.response.data.message;
                    }
                    this.$toast.error(errortxt, {
                        position: "top",
                        duration: 3000,
                    });

                    status = false;
                });
            return status;
        },
        //********* Create Exam with API **********/
        async updateExam() {
            let url =
                this.$store.getters.getAPIKey.examination +
                `/${this.examToBeUpdated.id}`;
            let status = null;
            await this.$axios
                .put(url, this.exam)
                .then((response) => {
                    status = true;
                    response;
                })
                .catch((error) => {
                    let errortxt = "Failed to Create";
                    if (error.response && error.response.status == 422) {
                        let errormsgs = error.response.data.errors;
                        for (const key in errormsgs) {
                            errortxt = errormsgs[key][0];
                        }
                    } else {
                        errortxt = error.response.data.message;
                    }
                    this.$toast.error(errortxt, {
                        position: "top",
                        duration: 3000,
                    });

                    status = false;
                });
            return status;
        },
        //********* Reset Or Clear Response **********/
        cancel() {
            this.$emit("examFormCancelled");
            const examData = {
                name: "",

                course_id: "",
                category_id: null,
                hidden: false,
                starts_at: "",
                end_at: "",
                limitted_time_seconds: "",
                maximum_mark: null,
                negative_mark: null,
                is_objective_only: false,
                shuffle_questions: false,
                shuffle_choices: false,
                exclude_from_report: false,
                ordered_choice_list: false,
                auto_evaluation: true,
                publish_result: false,
            };
            this.exam = examData;
        },
        clearResponse(status) {
            this.examBtn.text = "Submit";
            this.loadExams = false;
            setTimeout(() => {
                if (status) {
                    this.$emit("getExams");
                    this.cancel();
                }
                this.examBtn.disabled = false;
            }, 3000);
        },
        //******** Form Required DATA ****************/
        async getExamCategories() {
            const url = this.$store.getters.getAPIKey.examCategory;
            await this.$axios
                .get(url)
                .then((response) => {
                    let examCategories = response.data;
                    this.examCategoriesOptions = [{
                        value: null,
                        text: examCategories.length == 0 ?
                            "No categories found!" :
                            "Please Select One",
                        disabled: true,
                    }, ];
                    examCategories.forEach((category) => {
                        this.examCategoriesOptions.push({
                            value: `${category.id}`,
                            text: category.name,
                        });
                    });
                    response;
                })
                .catch((error) => {
                    error;
                });
        },
        // getMaximumAttemptsAndChoices() {
        //   this.maximumAttemptsAllowed = [
        //     { value: null, text: "Select Maximum Attempts allowed" },
        //   ];
        //   this.maxChoicesAllowed = [
        //     { value: null, text: "Select Maximum Choices allowed" },
        //   ];

        //   let maxAttempts = 5;
        //   let maxChoices = 5;
        //   for (let i = 0; i <= maxAttempts; i++) {
        //     this.maximumAttemptsAllowed.push({
        //       value: i,
        //       text: i,
        //     });
        //   }
        //   for (let i = 2; i <= maxChoices; i++) {
        //     this.maxChoicesAllowed.push({
        //       value: i,
        //       text: i,
        //     });
        //   }
        // },
        async getCourses() {
            this.loadCourses = true;
            const url =
                this.$store.getters.getAPIKey.mainAPI +
                `/institutionuser/courses/withusers`;
            await this.$axios
                .get(url)
                .then((response) => {
                    this.coursesOptions = [];
                    let courses = response.data;
                    this.coursesOptions = [{
                        value: null,
                        text: courses.length != 0 ? "Select Course" : "No Courses Found!",
                        disabled: true,
                    }, ];
                    courses.forEach((course) => {
                        this.coursesOptions.push({
                            value: `${course.id}`,
                            text: course.name +
                                " (" +
                                course.usersubgroup.usergroup.name +
                                " - " +
                                course.usersubgroup.name +
                                ")",
                                usergroup_id: course.usersubgroup.usergroup_id
                        });
                    });
                    this.coursesOptions.push({
                        value: "0",
                        text: "Not linked to course"
                    });
                    response;
                })
                .catch((error) => {
                    error;
                });
            this.fetchData = await false;
        },
    },
};
</script>
